import { useMemo, useState, type ReactNode } from "react";
import { format } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { IcCancel, IcTboContainer } from "@racoon/icons";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { Button, IconButton, Modal, ModalClose, ModalContent, ModalTitle, cn } from "@racoon/ui";
import { getJobPhotoSlides } from "~/shared/utils/jobPhotos";
import { JobPhotoCarousel } from "~/pages/records/components/job-photo-carousel";
import { JobStatusPill } from "~/pages/records/lib/job-status-pill";
import {
  formatCompletedAtHeader,
  formatContainerGroup,
  formatJobNumber,
  getJobCompletedAt,
  getJobTypeLabel,
  getNotCompletedReason,
} from "~/pages/records/lib/job-formatters";
import type { GpsStop } from "~/features/gps-stops";
import {
  SITE_REACH_M,
  containerFill,
  siteStops,
  truckNearSite,
  type ContainerFill,
} from "../model/photo-evidence";
import type { GpsTrackingPoint } from "../model/types";

const OFF_SITE_COLOR = "text-[#FF9500]";

/** Admin's photo report colours a container by how full it was. */
const fillColor = (value: number) => {
  if (value === 0) return "text-red-500";
  if (value <= 0.5) return "text-blue-600";
  if (value <= 0.75) return "text-violet-500";
  if (value <= 1) return "text-green-500";
  return "text-orange-500";
};

const fillLabel = (value: number) =>
  Number.isInteger(value) && value !== 0 ? value.toFixed(1) : String(value);

/** Inside the geofence, or on the site's point to within the metre a distance prints at. */
const atSite = (distanceM: number) => distanceM < 0.5;

const useFormatDistance = () => {
  const { lang } = useLang();
  return useMemo(() => {
    const metres = new Intl.NumberFormat(lang, {
      style: "unit",
      unit: "meter",
      maximumFractionDigits: 0,
    });
    const kilometres = new Intl.NumberFormat(lang, {
      style: "unit",
      unit: "kilometer",
      maximumFractionDigits: 1,
    });
    return (m: number) => (m < 1000 ? metres.format(m) : kilometres.format(m / 1000));
  }, [lang]);
};

const EvidenceRow = ({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) => (
  <div data-testid={testId} className="flex flex-col gap-1 border-b border-slate-200 py-2">
    <span className="text-xs leading-4 text-text-secondary">{label}</span>
    <div className="text-sm leading-5 text-slate-900">{children}</div>
  </div>
);

/** A container, coloured and labelled by how full it was. */
const BucketIcon = ({ value }: { value: number }) => (
  <span
    className={cn("relative flex size-7 shrink-0 items-center justify-center", fillColor(value))}
  >
    <IcTboContainer width={value === 0 ? 24 : 26} height={24} />
    <span className="absolute left-1/2 top-[calc(50%-4px)] -translate-x-1/2 text-[9px] font-bold leading-[9px] text-white">
      {fillLabel(value)}
    </span>
  </span>
);

/** The containers' fills, then the declared and the actual volume as plain numbers. */
const Containers = ({ fill }: { fill: ContainerFill }) => {
  const t = useT();
  const cubicMeters = (volume: number | undefined) =>
    volume === undefined
      ? "—"
      : t(Namespace.Govdash, "gpsTracking.evidence.cubicMeters", { volume });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {fill.coefficients.map((value, index) => (
          <BucketIcon key={index} value={value} />
        ))}
      </div>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
        <span className="text-xs leading-5 text-text-secondary">
          {t(Namespace.Govdash, "gpsTracking.evidence.declaredVolume")}
        </span>
        <span data-testid="photo-report-evidence-declared">{cubicMeters(fill.plannedVolume)}</span>
        <span className="text-xs leading-5 text-text-secondary">
          {t(Namespace.Govdash, "gpsTracking.evidence.factVolume")}
        </span>
        <span data-testid="photo-report-evidence-fact">{cubicMeters(fill.factVolume)}</span>
      </div>
    </div>
  );
};

/**
 * The evidence beside a job's photos: how close the truck came while the job ran, whether it
 * stood at the site, and how full the containers were. Rows without data are left out.
 */
const Evidence = ({
  job,
  track,
  stops,
}: {
  job: ConnectProto.Job;
  track: GpsTrackingPoint[];
  stops: GpsStop[];
}) => {
  const t = useT();
  const formatDistance = useFormatDistance();
  const truck = useMemo(() => truckNearSite(job, track), [job, track]);
  const stopsAtSite = useMemo(() => siteStops(job, stops), [job, stops]);
  const fill = useMemo(() => containerFill(job), [job]);

  const offSiteNote = t(Namespace.GpsTracking, "photoMarkerOffSite", {
    limit: String(SITE_REACH_M),
  });

  if (!truck && stopsAtSite.length === 0 && !fill) {
    return (
      <p data-testid="photo-report-evidence-empty" className="text-sm text-text-secondary">
        {t(Namespace.Govdash, "gpsTracking.evidence.empty")}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto">
      {truck && (
        <EvidenceRow
          testId="photo-report-evidence-truck"
          label={t(Namespace.Govdash, "gpsTracking.evidence.truck")}
        >
          <span className={cn(truck.offSite && OFF_SITE_COLOR, truck.offSite && "font-medium")}>
            {atSite(truck.distanceM)
              ? t(Namespace.Govdash, "gpsTracking.evidence.truckOnSite", {
                  time: format(truck.at, "HH:mm"),
                })
              : t(Namespace.Govdash, "gpsTracking.evidence.truckFromSite", {
                  distance: formatDistance(truck.distanceM),
                  time: format(truck.at, "HH:mm"),
                })}
            {truck.offSite && ` ${offSiteNote}`}
          </span>
        </EvidenceRow>
      )}

      {stopsAtSite.length > 0 && (
        <EvidenceRow
          testId="photo-report-evidence-stop"
          label={t(Namespace.Govdash, "gpsTracking.evidence.stop")}
        >
          {stopsAtSite.map((stop, index) => (
            <div key={index}>
              {format(stop.startAt, "HH:mm")}–{format(stop.endAt, "HH:mm")} ·{" "}
              {Math.max(1, Math.round(stop.durationSeconds / 60))}{" "}
              {t(Namespace.GpsTracking, "gpsStopUnitMinutes")}
            </div>
          ))}
        </EvidenceRow>
      )}

      {fill && (
        <EvidenceRow
          testId="photo-report-evidence-containers"
          label={t(Namespace.Govdash, "gpsTracking.evidence.containers")}
        >
          <Containers fill={fill} />
        </EvidenceRow>
      )}
    </div>
  );
};

interface GpsPhotoReportModalProps {
  /** The job whose photos are shown; the modal is closed while undefined. */
  job: ConnectProto.Job | undefined;
  /** The truck's whole track, to see where it was while the job ran. */
  track: GpsTrackingPoint[];
  stops: GpsStop[];
  onClose: () => void;
}

/** The carousel hands its instance up only for keyboard paging, which the two columns skip. */
const ignoreCarouselApi = () => {};

/**
 * A job's photo report over the movement control map, read-only: the photos before and after
 * the service side by side, as in admin's report (a missed pickup's photos go with "after"),
 * and the track's evidence beside them. A focused carousel pages with ←/→; Esc closes.
 */
export const GpsPhotoReportModal = ({ job, track, stops, onClose }: GpsPhotoReportModalProps) => {
  const t = useT();
  // Keeps the last job on screen while the modal animates out.
  const [shownJob, setShownJob] = useState(job);
  if (job && job !== shownJob) setShownJob(job);
  const isOpen = job != null;

  const { before, after } = useMemo(() => {
    const slides = getJobPhotoSlides(shownJob);
    return {
      before: slides.filter((slide) => slide.group === "before"),
      after: slides.filter((slide) => slide.group !== "before"),
    };
  }, [shownJob]);
  const completedAt = shownJob ? getJobCompletedAt(shownJob) : null;
  const isNotCompleted = shownJob?.status === ConnectProto.JobStatus.NOT_COMPLETED;
  // The MVO leads: an auditor holds the MVO to account. Without one the site takes its place.
  const mvo = shownJob?.serviceProvider?.name;
  const site = shownJob?.pickupLocation?.name;
  const title = mvo || site || "";
  const subtitle = shownJob
    ? [
        mvo ? site : undefined,
        formatJobNumber(shownJob),
        formatContainerGroup(shownJob),
        getJobTypeLabel(t, shownJob.type),
      ]
        .filter((part) => part?.trim())
        .join(" · ")
    : "";

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        size="custom"
        showClose={false}
        aria-describedby={undefined}
        data-testid="photo-report"
        // `!`: @racoon/ui's cn only joins classes, so the size variant's own md:w-full would
        // otherwise win.
        className="overflow-hidden md:h-[min(720px,calc(100vh-4rem))] md:!w-[min(1200px,calc(100vw-4rem))]"
      >
        {shownJob && (
          <>
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-4 py-3">
              <div className="flex min-w-0 flex-col gap-1">
                <ModalTitle
                  data-testid="photo-report-title"
                  className="truncate !text-base !leading-5 text-slate-900"
                  title={title}
                >
                  {title}
                </ModalTitle>
                <p
                  data-testid="photo-report-subtitle"
                  className="truncate text-sm leading-4 text-text-secondary"
                  title={subtitle}
                >
                  {subtitle}
                </p>
              </div>

              <div className="flex max-w-[480px] items-center gap-2">
                <JobStatusPill status={shownJob.status} />
                {completedAt && (
                  <span className="shrink-0 text-sm font-semibold leading-4 text-slate-900">
                    {formatCompletedAtHeader(completedAt)}
                  </span>
                )}
                {isNotCompleted && (
                  <span className="truncate text-sm leading-4 text-brand-red">
                    {getNotCompletedReason(shownJob.driverComment) ??
                      t(Namespace.Govdash, "wasteExport.reasonNotSpecified")}
                  </span>
                )}
              </div>

              <ModalClose asChild>
                <IconButton
                  className="justify-self-end"
                  aria-label={t(Namespace.Govdash, "actions.close")}
                >
                  <IcCancel />
                </IconButton>
              </ModalClose>
            </div>

            <div className="flex min-h-0 flex-1 gap-4 px-4 pb-4">
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-4">
                <div data-testid="photo-report-before" className="min-w-0">
                  <JobPhotoCarousel
                    key={String(shownJob.id)}
                    slides={before}
                    onApiChange={ignoreCarouselApi}
                  />
                </div>
                <div data-testid="photo-report-after" className="min-w-0">
                  <JobPhotoCarousel
                    key={String(shownJob.id)}
                    slides={after}
                    onApiChange={ignoreCarouselApi}
                  />
                </div>
              </div>

              <aside
                data-testid="photo-report-evidence"
                className="flex w-72 shrink-0 flex-col gap-2"
              >
                <p className="text-base font-semibold leading-5 text-slate-900">
                  {t(Namespace.Govdash, "gpsTracking.evidence.title")}
                </p>
                <Evidence job={shownJob} track={track} stops={stops} />
                <div className="mt-auto flex flex-col gap-2 pt-2">
                  {/* A new tab: the track, the player and the filters stay as they are. */}
                  <a
                    href={`/records/waste-export/${shownJob.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="photo-report-registry-link"
                    className="text-sm font-medium text-brand-blue hover:underline"
                  >
                    {t(Namespace.Govdash, "gpsTracking.openInRegistry")}
                  </a>
                  <ModalClose asChild>
                    <Button className="w-full">{t(Namespace.Govdash, "actions.close")}</Button>
                  </ModalClose>
                </div>
              </aside>
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
